// Imports
import { faPrint } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Params, useParams } from 'react-router-dom';
import styled from 'styled-components/macro';
import { useAppDispatch } from '../../../../../../../shareables/foundation/front-end/redux/hooks';
import apiActions from '../../../../../../../shareables/integrations/api/redux/actions';
import { GetRecipeResponse } from '../../../../../../../shareables/types/api/main/website/page/type/213/recipe/get';
import Byline from '../../shared-components/byline';
import getHumanReadableTime from '../utils/get-human-readable-time';


// Styled components
const Container = styled.div`
	h3 {
		font-size: 1.5rem;
		font-weight: 500;
		line-height: 1.1;
	}
	
	hr {
		margin-top: 1.25rem;
		margin-bottom: 1.25rem;
		border: 0;
		border-top: 0.0625rem solid #eee;
	}
`;

const Print = styled.button`
	padding: 0.0625rem 0.375rem;
	line-height: 1.5;
	border-radius: 0.1875rem;
	background-color: #fff;
	border: 0.0625rem solid #ccc !important;
	height: 1.75rem;
	text-decoration: none !important;
	
	border: 0;
	cursor: pointer;
	white-space: nowrap;
	
	&:hover {
		background-color: #e6e6e6 !important;
	}
	
	.svg-inline--fa {
		margin-right: 0.25rem;
	}
`;

const Heading = styled.h3`
	margin-bottom: 0.625rem;
`;

const Ingredients = styled.ul`
	list-style: none;
	margin-left: 0;
`;

const Directions = styled.p`
	white-space: pre-line;
	margin-bottom: 0.625rem;
`;


// Define the accepted props
interface Props {
	pageID: number;
	pageTitle: string;
	storybookRecipe?: GetRecipeResponse;
}


// Function component
const Recipe: React.FC<Props> = ({ pageID, pageTitle, storybookRecipe }) => {
	// Use state
	const [recipe, setRecipe] = useState<GetRecipeResponse | null>(storybookRecipe ?? null);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Use React Router functionality
	const { recipeIdentifier } = useParams<Params>();
	
	
	// Use ref
	const containerRef = useRef<HTMLDivElement>(null);
	
	
	// Load recipe on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetRecipeResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/page/id_${pageID}/type/213/recipe/${recipeIdentifier ?? ''}`,
				completion: (json) => {
					setRecipe(json.response);
				},
			})
		);
	}, [dispatch, pageID, recipeIdentifier]);
	
	
	// Handle printing
	const handlePrint = () => {
		// Do no work if data is loading
		if (recipe === null || !containerRef.current) {
			return;
		}
		
		
		// Get the current document styles
		const styles = Array.from(document.styleSheets)
			.map((sheet) => {
				try {
					// Get rules
					const rules = Array.from(sheet.cssRules || []);
					
					
					// Return
					return rules.map((rule) => rule.cssText).join('\n');
				} catch (e) {
					// Ignore stylesheets that we can't access
					return '';
				}
			})
			.join('\n');
		
		
		// Open a new tab and add existing styles
		const newTabWindow = window.open();
		newTabWindow?.document.write(`<style>${styles}</style>`);
		
		
		// Modify spacing
		newTabWindow?.document.write(`
			<style>
				body {
					margin: 2rem;
					min-height: unset; /* If not, there will be an additional blank page */
				}
			</style>`);
		
		
		// Add custom styles
		const customStyles = `
						div:first-of-type{
							line-height: 1.1;
						}
						
						h3 {
							margin-bottom: 1rem;
						}
						
						hr {
							margin-top: 1.25rem;
							margin-bottom: 1.25rem;
							border: 0;
							border-top: .0625rem solid #eee;
						}
						
						ul {
							list-style: none;
						}
				
				`;
		
		newTabWindow?.document.write(`<style>${customStyles}</style>`);
		
		
		// Get current content
		const currentContent = containerRef.current.innerHTML;
		
		
		// Form HTML content for printing
		const printContent = currentContent
			// Change recipe name heading
			.replace(/<h3>(.*?)<\/h3>/, `<h1>$1</h1>`)
			// Replace "By" line
			.replace(
				/<small.*?<\/small>/,
				`${recipe.submitter?.formattedName ? `<small><em>By ${recipe.submitter.formattedName}</em></small>` : ''}`
			)
			// Remove the print button
			.replace(/<button.*?<\/button>/, '');
		
		
		// Add print content
		newTabWindow?.document.write('<body>');
		newTabWindow?.document.write(printContent);
		newTabWindow?.document.write('</body></html>');
		
		
		// Print and close
		newTabWindow?.document.close();
		newTabWindow?.focus();
		
		newTabWindow?.addEventListener(
			'afterprint',
			() => {
				newTabWindow?.close();
			},
			{ once: true }
		);
		
		newTabWindow?.print();
	};
	
	
	// If loading, then display nothing
	if (recipe === null) {
		return null;
	}
	
	
	// Return JSX
	return (
		<Container ref={containerRef}>
			<Helmet title={`${recipe.name} | ${pageTitle}`} />
			
			<div>
				<h3>{recipe.name}</h3>
				<Byline className='is-gray' post={recipe} created={recipe.submitted} action='submitted' />
				<Print onClick={handlePrint}>
					<FontAwesomeIcon icon={faPrint} />
					Print Recipe
				</Print>
			</div>
			
			<hr />
			
			<div>
				<Heading>Time to make</Heading>
				<div>
					Prep time: <strong>{getHumanReadableTime(recipe.prepTime)}</strong>
				</div>
				<div>
					Cook time: <strong>{getHumanReadableTime(recipe.cookTime)}</strong>
				</div>
				<div>
					Total time: <strong>{getHumanReadableTime(recipe.prepTime + recipe.cookTime)}</strong>
				</div>
			</div>
			
			<hr />
			
			<div>
				<Heading>Ingredients</Heading>
				<Ingredients>
					{recipe.ingredients.split('\n').map((ingredient, i) => (
						<li key={i}>{ingredient}</li>
					))}
				</Ingredients>
			</div>
			
			<hr />
			
			<div>
				<Heading>Directions</Heading>
				<Directions>{recipe.directions}</Directions>
			</div>
		</Container>
	);
};

export default Recipe;
